import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileUp, ScanLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { ImportWizard } from './ImportWizard'
import { listImportBatches, type ImportBatch } from './importApi'

/**
 * Nomination-list imports — the history, and the way to start another.
 *
 * ## Why the tab has a page and not just a button
 *
 * An import is not a one-off action, it is a RECORD. "Where did these four thousand
 * candidacies come from" is asked months later, usually by somebody who was not in the room,
 * and the answer is a batch: which file, read when, how many rows it rejected and who
 * applied it. A button hidden on the register would do the import and lose the account of it.
 *
 * ## Failed batches are listed, not swept up
 *
 * A batch that could not be read is the evidence that somebody tried. It stays in the list
 * with its reason, which is also how the person who tried finds their way back to it.
 */
export function ImportPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const [batches, setBatches] = useState<ImportBatch[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const open = params.get('import') === '1'

  // `electionId` IS in the dependency array. Omitting it binds the loader to the null seen on
  // the first render, and the page stays permanently empty with nothing saying why.
  const load = useCallback(async () => {
    if (!electionId) { setBatches([]); return }
    try {
      setError(null)
      setBatches(await listImportBatches(electionId))
    } catch (err) {
      setError(explain(err))
      setBatches([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A nomination-list import" />

  const start = () => setParams((p) => { p.set('import', '1'); return p }, { replace: true })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Nomination-list imports</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Load a ballot from a spreadsheet or a scanned form. Every import is checked in full
            before anything is created, and nothing is applied until somebody has read the
            verdict.
          </p>
        </div>
        <button type="button" onClick={start}
          className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          <FileUp className="h-3.5 w-3.5" /> New import
        </button>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {batches !== null && batches.length === 0 && !error && (
        <>
          <EmptyState
            title="No imports yet"
            hint="A nomination list can come from a spreadsheet, or from a photograph or scan of
                  a nomination paper — a machine reads the page and the same checks run either way." />
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
            <button type="button" onClick={start} className="erp-card px-3 py-3 text-left hover:border-chocolate transition-colors">
              <FileUp className="h-4 w-4 mb-1.5 text-chocolate" />
              <div className="text-erp-sm font-medium">From a spreadsheet</div>
              <p className="text-erp-xs text-muted-foreground mt-1">
                A CSV of seats, units, candidates and parties.
              </p>
            </button>
            <button type="button" onClick={start} className="erp-card px-3 py-3 text-left hover:border-chocolate transition-colors">
              <ScanLine className="h-4 w-4 mb-1.5 text-chocolate" />
              <div className="text-erp-sm font-medium">From a scanned form</div>
              <p className="text-erp-xs text-muted-foreground mt-1">
                A photo, scan or PDF of a nomination paper. You see what the machine read
                before anything is created.
              </p>
            </button>
          </div>
        </>
      )}

      {(batches === null || batches.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Source</Th>
            <Th>File</Th>
            <Th>Status</Th>
            <Th className="text-right">Rows</Th>
            <Th className="text-right">Ready</Th>
            <Th className="text-right">Rejected</Th>
            <Th className="text-right">Created</Th>
            <Th>Uploaded</Th>
          </>
        )}>
          {batches === null ? <LoadingRows colSpan={8} />
            : batches.map((b) => (
              <tr key={b.id}>
                <Td>
                  <span className="inline-flex items-center gap-1.5 text-erp-xs">
                    {b.sourceCode === 'OCR_FORM'
                      ? <><ScanLine className="h-3 w-3 text-muted-foreground" /> Scanned form</>
                      : <><FileUp className="h-3 w-3 text-muted-foreground" /> Spreadsheet</>}
                  </span>
                </Td>
                <Td className="text-erp-xs">{b.notes ?? '—'}</Td>
                <Td>
                  <StatusChip code={b.statusCode} />
                  {b.error && (
                    <span className="block mt-0.5 text-erp-xs text-red-700 dark:text-red-400 max-w-md">
                      {b.error}
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums">{fmtNumber(b.totalRows)}</Td>
                <Td className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                  {fmtNumber(b.validRows)}
                </Td>
                <Td className={cn('text-right tabular-nums',
                  b.rejectedRows > 0 && 'text-red-700 dark:text-red-400')}>
                  {fmtNumber(b.rejectedRows)}
                </Td>
                <Td className="text-right tabular-nums">
                  {b.statusCode === 'APPLIED' ? fmtNumber(b.appliedRows) : '—'}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">{fmtDateTime(b.createdAt)}</Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        Applying an import creates candidacies at <strong>prospective</strong>. It does not
        clear anybody for a ballot — certification is a separate, separately-authorised step
        on the ballot register.
      </Note>

      <ImportWizard
        open={open}
        electionId={electionId}
        onClose={() => setParams((p) => { p.delete('import'); return p }, { replace: true })}
        onApplied={load}
      />
    </div>
  )
}

/** UPLOADED → PARSING → VALIDATED → APPLIED, plus FAILED. */
function StatusChip({ code }: { code: string }) {
  const tone = code === 'APPLIED' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'
    : code === 'FAILED' ? 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300'
      : code === 'VALIDATED' ? 'bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200'
        : 'bg-muted text-muted-foreground'
  const label = code === 'UPLOADED' ? 'Uploaded, not read'
    : code === 'VALIDATED' ? 'Checked, not applied'
      : code.charAt(0) + code.slice(1).toLowerCase()
  return (
    <span className={cn('inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-medium', tone)}>
      {label}
    </span>
  )
}
